import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Smartphone, CheckCircle2, XCircle, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { getValidAccessToken } from "@/lib/supabase/getValidAccessToken";

interface NequiPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  planId: string;
  planName: string;
  amount: number;
  onSuccess?: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8001";

export const NequiPaymentModal: React.FC<NequiPaymentModalProps> = ({
  isOpen,
  onClose,
  planId,
  planName,
  amount,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [step, setStep] = useState<"input" | "waiting" | "success" | "error">("input");
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(90);
  const [errorMessage, setErrorMessage] = useState("");

  // Pre-fill phone number from profile if saved
  useEffect(() => {
    if (user?.user_metadata?.nequi_phone) {
      setPhoneNumber(user.user_metadata.nequi_phone);
    } else if (user?.user_metadata?.phone) {
      setPhoneNumber(user.user_metadata.phone);
    }
  }, [user]);

  // Reset modal state on open
  useEffect(() => {
    if (isOpen) {
      setStep("input");
      setLoading(false);
      setReference(null);
      setCountdown(90);
      setErrorMessage("");
    }
  }, [isOpen]);

  // Countdown timer for Push notification waiting step
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (step === "waiting" && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (step === "waiting" && countdown === 0) {
      setStep("error");
      setErrorMessage("La notificación Push expiró. Abre la app de Nequi e intentalo de nuevo.");
    }
    return () => clearInterval(timer);
  }, [step, countdown]);

  // Polling payment status every 3 seconds while in waiting step
  useEffect(() => {
    let pollInterval: NodeJS.Timeout;

    const checkStatus = async () => {
      if (!reference) return;

      try {
        const token = await getValidAccessToken();
        if (!token) return;

        const response = await fetch(`${API_BASE_URL}/api/v1/payments/nequi/status/${reference}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.status === "APPROVED") {
            setStep("success");
            toast.success("¡Pago con Nequi Aprobado exitosamente!");
            window.dispatchEvent(new CustomEvent("subscription_updated"));
            onSuccess?.();
          } else if (data.status === "REJECTED" || data.status === "EXPIRED" || data.status === "ERROR") {
            setStep("error");
            setErrorMessage(data.status_message || "El pago fue rechazado en tu App Nequi.");
          }
        }
      } catch (err) {
        console.error("Error polling Nequi status:", err);
      }
    };

    if (step === "waiting" && reference) {
      pollInterval = setInterval(checkStatus, 3000);
    }

    return () => clearInterval(pollInterval);
  }, [step, reference, onSuccess]);

  const handleInitiatePayment = async (e: React.FormEvent) => {
    e.preventDefault();

    const cleanPhone = phoneNumber.trim().replace(/\s+/g, "");
    if (!/^\d{10}$/.test(cleanPhone) || !cleanPhone.startsWith("3")) {
      toast.error("Ingresa un número celular Nequi válido de 10 dígitos (ej: 3001234567)");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const token = await getValidAccessToken();
      if (!token) {
        throw new Error("No se encontró una sesión activa. Por favor inicia sesión nuevamente.");
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/payments/nequi/initiate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          phone_number: cleanPhone,
          amount: amount,
          plan_id: planId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Error iniciando la transacción Nequi");
      }

      setReference(data.reference);
      setStep("waiting");
      setCountdown(90);
      toast.info("Notificación enviada. Revisa tu celular.");
    } catch (err: any) {
      toast.error(err.message || "No se pudo conectar con el servicio Nequi");
      setErrorMessage(err.message || "Error al solicitar notificación Push.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md rounded-2xl bg-white p-6 shadow-2xl border-0">
        <DialogHeader className="flex flex-col items-center text-center">
          <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#200020] text-pink-500 shadow-md">
            <Smartphone className="h-7 w-7 text-[#DA0081]" />
          </div>
          <DialogTitle className="text-xl font-bold text-gray-900">
            Pago con Nequi
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Plan {planName} • ${amount.toLocaleString("es-CO")} COP
          </DialogDescription>
        </DialogHeader>

        {step === "input" && (
          <form onSubmit={handleInitiatePayment} className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nequi-phone" className="text-sm font-medium text-gray-700">
                Número Celular Nequi
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-sm font-semibold text-gray-400">
                  +57
                </span>
                <Input
                  id="nequi-phone"
                  type="tel"
                  placeholder="300 123 4567"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  maxLength={10}
                  className="pl-12 rounded-xl border-gray-200 text-base font-medium focus:border-[#DA0081] focus:ring-[#DA0081]"
                  required
                />
              </div>
              <p className="text-xs text-gray-500">
                Te enviaremos una notificación Push a la app Nequi en este número.
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-[#200020] font-bold text-white shadow-lg transition-all hover:bg-[#2b0d2b] active:scale-[0.98]"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                "Enviar Notificación Push"
              )}
            </Button>
          </form>
        )}

        {step === "waiting" && (
          <div className="mt-4 flex flex-col items-center text-center space-y-4 py-4">
            <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-pink-50">
              <div className="absolute h-full w-full animate-ping rounded-full bg-[#DA0081]/20"></div>
              <Smartphone className="h-10 w-10 text-[#DA0081]" />
            </div>

            <div className="space-y-1">
              <h4 className="text-lg font-bold text-gray-900">
                Revisa tu App Nequi
              </h4>
              <p className="text-sm text-gray-600 px-4">
                Hemos enviado una notificación al número <span className="font-semibold text-gray-900">{phoneNumber}</span>. Abre Nequi y aprueba la solicitud.
              </p>
            </div>

            <div className="flex items-center gap-2 rounded-full bg-gray-100 px-4 py-1.5 text-xs font-semibold text-gray-700">
              <ClockIcon className="h-4 w-4 text-[#DA0081]" />
              <span>Expiración en {countdown} segundos</span>
            </div>

            <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-[#DA0081]" />
              <span>Verificando estado del pago en tiempo real...</span>
            </div>
          </div>
        )}

        {step === "success" && (
          <div className="mt-4 flex flex-col items-center text-center space-y-4 py-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CheckCircle2 className="h-10 w-10" />
            </div>
            <div className="space-y-1">
              <h4 className="text-lg font-bold text-gray-900">
                ¡Pago Exitoso!
              </h4>
              <p className="text-sm text-gray-600">
                Tu plan <span className="font-semibold">{planName}</span> ha sido activado correctamente.
              </p>
            </div>
            <Button
              onClick={onClose}
              className="w-full rounded-xl bg-emerald-600 font-bold text-white hover:bg-emerald-700"
            >
              Comenzar a Usar
            </Button>
          </div>
        )}

        {step === "error" && (
          <div className="mt-4 flex flex-col items-center text-center space-y-4 py-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <XCircle className="h-10 w-10" />
            </div>
            <div className="space-y-1">
              <h4 className="text-lg font-bold text-gray-900">
                No pudimos procesar el pago
              </h4>
              <p className="text-sm text-rose-600 px-2">
                {errorMessage || "Transacción no aprobada o tiempo de espera agotado."}
              </p>
            </div>
            <Button
              onClick={() => setStep("input")}
              className="w-full rounded-xl bg-gray-900 font-bold text-white hover:bg-gray-800 flex items-center justify-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Reintentar Pago
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ClockIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" strokeWidth="2" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6l4 2" />
  </svg>
);
